import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Paper, Typography, Tooltip as MuiTooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../i18n/formatters';

interface FunktionarEngagement {
    personalNumber: string;
    name: string;
    functionText: string;
    startDate: string | null;
    endDate: string | null;
    organizationNumber: string;
    organizationName: string;
    paymentOrders?: string;
}

interface EngagementDebtChartProps {
  data: FunktionarEngagement[];
  minDate?: number;
  maxDate?: number;
}

const EngagementDebtChart: React.FC<EngagementDebtChartProps> = ({ data, minDate: propMinDate, maxDate: propMaxDate }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const today = new Date();
  
  let minDate = propMinDate || 0;
  let maxDate = propMaxDate || 0;

  // Calculate domain if not provided
  if (!propMinDate || !propMaxDate) {
      let hasActiveRoles = false;
      
      // Temporary vars to find range from data
      let dataMin = today.getTime();
      let dataMax = 0;

      data.forEach(item => {
        const start = item.startDate ? new Date(item.startDate).getTime() : today.getTime();
        const end = item.endDate ? new Date(item.endDate).getTime() : today.getTime();
        
        if (item.endDate === null) hasActiveRoles = true;
        
        if (start < dataMin) dataMin = start;
        if (end > dataMax) dataMax = end;
      });
      
      if (hasActiveRoles) {
        dataMax = Math.max(dataMax, today.getTime());
      }
      
      const padding = (dataMax - dataMin) * 0.05;
      
      minDate = dataMin - padding;
      maxDate = hasActiveRoles ? today.getTime() : dataMax + padding;
      
      // Fallback if dataMin was actually after today (empty/future data?)
      if (minDate > maxDate) minDate = maxDate - (1000 * 60 * 60 * 24 * 30);
  }
  
  // Calculate cutoff date for payment orders: 1 year prior to the absolute earliest engagement date
  let globalEarliestStart = today.getTime();
  data.forEach(d => {
    const t = d.startDate ? new Date(d.startDate).getTime() : today.getTime();
    if (t < globalEarliestStart) globalEarliestStart = t;
  });
  const paymentOrderCutoffDate = new Date(globalEarliestStart);
  paymentOrderCutoffDate.setFullYear(paymentOrderCutoffDate.getFullYear() - 1);
  const paymentOrderCutoff = paymentOrderCutoffDate.getTime(); 

  // Clamp minDate to filtered payment order range
  if (minDate < paymentOrderCutoff) {
      minDate = paymentOrderCutoff;
  }

  const domainMin = minDate;
  const domainMax = maxDate; 

  // Group by organization
  const groupedByOrg: Record<string, FunktionarEngagement[]> = {};
  data.forEach(item => {
    if (!groupedByOrg[item.organizationNumber]) {
      groupedByOrg[item.organizationNumber] = [];
    }
    groupedByOrg[item.organizationNumber].push(item);
  });

  // Sort companies (must match Gantt chart sorting)
  const sortedOrgNumbers = Object.keys(groupedByOrg).sort((a, b) => {
    const rolesA = groupedByOrg[a];
    const rolesB = groupedByOrg[b];
    const maxA = Math.max(...rolesA.map(r => r.startDate ? new Date(r.startDate).getTime() : 0));
    const maxB = Math.max(...rolesB.map(r => r.startDate ? new Date(r.startDate).getTime() : 0));
    return maxB - maxA;
  });

  const chartData: any[] = [];
  const engagementData: any[] = [];
  const yAxisMap: any[] = [];

  sortedOrgNumbers.forEach((orgNum, index) => {
     const roles = groupedByOrg[orgNum];
     const orgName = roles[0].organizationName;
     
     yAxisMap.push({
         index: index,
         name: orgName,
         orgNum: orgNum
     });

     // Collect engagement periods
     roles.forEach(role => {
         const start = role.startDate ? new Date(role.startDate).getTime() : today.getTime();
         const end = role.endDate ? new Date(role.endDate).getTime() : domainMax; // Use domainMax for active roles to fill the chart
         
         if (end > start) {
             engagementData.push({
                 x: start,
                 y: index,
                 duration: end - start,
                 details: role,
                 isActive: role.endDate === null
             });
         }
     });

     // Collect all payment orders for this company
     const roleWithOrders = roles.find(r => r.paymentOrders);
     if (roleWithOrders && roleWithOrders.paymentOrders) {
         try {
             const orders = JSON.parse(roleWithOrders.paymentOrders);
             
             // 1. Collect valid orders first to determine min/max for normalization
             // OLD Filter logic: e.g. 6 months prior to earliest role (REMOVED to show all)
             // const earliestStart = Math.min(...roles.map(r => r.startDate ? new Date(r.startDate).getTime() : today.getTime()));
             // const thresholdDate = new Date(earliestStart);
             // thresholdDate.setMonth(thresholdDate.getMonth() - 6);

             const validOrders: any[] = [];
             
             Object.keys(orders).forEach(dateStr => {
                 const details = orders[dateStr];
                 // Filter for status = 'A', but also include entries with medelsslag (Public claims)
                 if (details.status !== 'A' && !details.medelsslag) return;

                 const orderDate = new Date(dateStr).getTime();
                 
                 // Filter out orders older than 1 year before the first engagement date
                 if (orderDate < paymentOrderCutoff) return;

                 // Show all orders
                 validOrders.push({
                     dateStr: dateStr,
                     date: orderDate,
                     details: orders[dateStr]
                 });
             });

             if (validOrders.length > 0) {
                 const amounts = validOrders.map(o => o.details.skuldsaldo || 0);
                 const minAmount = Math.min(...amounts);
                 const maxAmount = Math.max(...amounts);
                 
                 validOrders.forEach(order => {
                     const amount = order.details.skuldsaldo || 0;
                     
                     // Normalize height (e.g., between 4px and 24px)
                     const minHeight = 4;
                     const maxHeight = 24;
                     let normHeight = minHeight;
                     
                     if (maxAmount > minAmount) {
                         normHeight = minHeight + ((amount - minAmount) / (maxAmount - minAmount)) * (maxHeight - minHeight);
                     } else {
                         normHeight = (minHeight + maxHeight) / 2; // Flat if all same
                     }

                     // Refined color logic:
                     // Status 'Utslag' -> Red (#D32F2F)
                     // Public Debt (Allmänna mål) -> Green (#4CAF50). Identified by medelsslag not being 'E'.
                     // Private Debt (Enskilda mål) -> Orange (#F57C00). Identified by medelsslag 'E' or missing.
                     const medel = order.details.medelsslag;
                     const isPublic = medel && medel !== 'EMAL'; 

                     chartData.push({
                         x: order.date,
                         y: index,
                         amount: amount,
                         normHeight: normHeight,
                         date: order.dateStr,
                         orgName: orgName,
                         details: order.details,
                         color: isPublic ? '#4CAF50' : '#F57C00'
                     });
                 });
             }

         } catch(e) {
             console.error("Failed to parse payment orders", e);
         }
     }
  });

  const CustomYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const item = yAxisMap[payload.value];
    
    if (!item) return null;

    return (
        <text x={x} y={y} dy={4} textAnchor="end" fill={theme.palette.text.primary} fontSize={11}
          textLength={item.name.length > 30 ? 185 : undefined} lengthAdjust="spacingAndGlyphs">
          {item.name}
        </text>
    );
  };

  const BarShape = (props: any) => {
      const { cx, cy, payload } = props;
      const height = payload.normHeight;
      const width = 6;
      
      // Center the bar horizontally on cx
      // Bottom align on cy + something? 
      // cy is the center of the band. If band is ~30px, we want it within that band.
      // Let's center it vertically around cy for now, or sit it on a "baseline" near cy + 10
      const y = cy + 10 - height; 

      return (
          <rect 
            x={cx - width/2} 
            y={y} 
            width={width} 
            height={height} 
            fill={payload.color} 
            rx={2}
            opacity={0.8}
          />
      );
  };

  const EngagementShape = (props: any) => {
    const { cy, xAxis, payload } = props;
    const { x, duration, isActive } = payload;
    
    // Calculate width relative to XAxis
    // x is the coordinate start. We need the width in pixels.
    // axis.scale(x + duration) - axis.scale(x)
    const scale = xAxis.scale;
    const xEnd = scale(x + duration);
    const xStart = scale(x);
    const width = Math.max(0, xEnd - xStart);
    
    // Height of the background strip
    const height = 26; 
    const y = cy - height / 2;

    const fillColor = isActive 
        ? theme.palette.action.selected 
        : 'rgba(25, 118, 210, 0.15)';

    return (
        <rect 
          x={xStart} 
          y={y} 
          width={width} 
          height={height} 
          fill={fillColor}
          rx={2}
        />
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      // Find the payload logic that isn't the background rect if both are active?
      // Recharts might trigger tooltip for the background shape too.
      // We prioritize showing debt info if available.
      const debtData = payload.find((p: any) => p.payload.amount !== undefined);
      const data = debtData ? debtData.payload : payload[0].payload;
      
      if (data.amount === undefined) return null; // Don't show tooltip for just the background engagement

      return (
        <Paper sx={{ p: 2, backgroundColor: theme.palette.background.paper, boxShadow: theme.shadows[4] }}>
          <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
            {formatCurrency(data.amount, i18n.resolvedLanguage)}
          </Typography>
          <Typography variant="caption" display="block">
            {t('common.fields.date')}: {data.date}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const ticks = React.useMemo(() => {
    const t = [];
    
    // Always include year markers
    const startYear = new Date(domainMin).getFullYear();
    const endYear = new Date(domainMax).getFullYear();
    
    for (let year = startYear; year <= endYear + 1; year++) {
        const date = new Date(year, 0, 1);
        const time = date.getTime();
        if (time >= domainMin && time <= domainMax) {
            t.push(time);
        }
    }
    
    // Ensure we don't have gaps if the domain starts/ends mid-year without a tick
    // Add start/end points if they are significant distances from existing ticks?
    // Actually, user asked to ensure "All years in the range have a label".
    // If range is [June 2023, June 2024], ticks: 2024-01-01.
    // 2023 is not labeled.
    // Let's force add a label for the start year if it's missing
    const hasStartYear = t.some(time => new Date(time).getFullYear() === startYear);
    if (!hasStartYear) {
         // Add label at start of domain, or closest Jan 1st even if outside?
         // Adding start of domain makes it irregular but labeled.
         // t.push(domainMin); (Disabled per standard charting practices usually, but user asked for coverage)
         // Better: Use start of that year? No, it's hidden.
         // Let's stick to Jan 1st ticks. If the user strictly wants YYYY-MM, maybe showing '2023-06' is fine.
         t.push(domainMin);
    }
    
    // Sort ticks
    t.sort((a, b) => a - b);
    
    // Remove duplicates
    return Array.from(new Set(t));
  }, [domainMin, domainMax]);

  if (!data || data.length === 0) return null;

  return (
    <Box sx={{ width: '100%', height: Math.max(300, sortedOrgNumbers.length * 50 + 80), display: 'flex', flexDirection: 'column' }}>
       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
         <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
          {t('personSearch.timelineTitle')}
        </Typography>
        <MuiTooltip 
            title={
                <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>{t('personSearch.chartHelp.title')}</Typography>
                    <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.75rem' }}>
                        <li>{t('personSearch.chartHelp.rows')}</li>
                        <li>{t('personSearch.chartHelp.background')}</li>
                        <li>{t('personSearch.chartHelp.vertical')}</li>
                        <li>{t('personSearch.chartHelp.height')}</li>
                        <li>{t('personSearch.chartHelp.colors')}</li>
                    </ul>
                </Box>
            }
            arrow
            placement="right"
        >
            <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
        </MuiTooltip>
       </Box>
       <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            margin={{ top: 20, right: 20, left: 10, bottom: 35 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} stroke={theme.palette.divider} />
            <XAxis
              type="number"
              dataKey="x"
              domain={[domainMin, domainMax]}
              tickFormatter={(value) => format(new Date(value), 'yyyy')}
              ticks={ticks}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              type="number"
              dataKey="y"
              name="organization"
              tickCount={sortedOrgNumbers.length}
              domain={[0, sortedOrgNumbers.length - 1]}
              tick={<CustomYAxisTick />}
              interval={0}
              width={200}
              padding={{ top: 20, bottom: 20 }}
              reversed={true} // Recharts scatter Y is weird? No, but typically index 0 is bottom. We want index 0 top if Gantt is top. 
              // Wait, Gantt uses "category" axis which is top-down usually. 
              // Scatter Y numeric: 0 is bottom. 
              // We need to reverse it to match Gantt if Gantt draws 0 at top. 
              // Let's check Gantt... BarChart layout="vertical". First item is top. 
              // So here index 0 should be top. 
              // In numeric YAxis, larger values are top. So index 0 (top org) needs to be at top value? 
              // Actually easier is to set reversed={true} so 0 is top.
            />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={engagementData} shape={<EngagementShape />} isAnimationActive={false} legendType="none" tooltipType="none" />
            <Scatter data={chartData} shape={<BarShape />} isAnimationActive={false} />
          </ScatterChart>
        </ResponsiveContainer>
       </Box>
    </Box>
  );
};

export default EngagementDebtChart;
